import { useState } from 'react'
import type { TmsInvoiceRow, TmsLoadRow, TmsLoadsReadiness } from '@numeo/types-main-web-app'
import { Button, Checkbox } from '@/components/ds2'
import { formatUsd, laneOf, LOAD_STAGE_LABEL } from './format'
import { amazonRowsOf } from './cardResults'
import { InvoiceApproval } from './InvoiceApproval'
import { LoadFixActions } from './LoadFixActions'
import { byFixOrder, documentActions, findingsOf, fixesOf, invoiceLoadsMessage, type RowAction } from './rowActions'
import { documentSlotsOf, LoadRow } from './LoadRow'
import { loadKeyOf } from './loadKey'
import { AMAZON_STATE, invoiceLabelOf, readyStateOf } from './loadRowStates'
import { useFreshRows } from './freshRows'
import { InvoicedLabel } from './InvoicedLabel'
import { CANNOT_INVOICE_NOTE, useCanCreateInvoices } from './useCanCreateInvoices'
import { useInvoicedLookup } from './threadInvoices'
import { useUpdatedBelow } from './updatedBelow'

export { invoiceLoadsMessage }

/** The invoiced rows a block draws before "Show N more": the rest sit behind one button at the bottom of the block. */
const INVOICED_SHOWN = 10

/**
 * The unambiguous reference a candidate of several loads is sent as: `#number (broker, lane)`, which names one load where the bare number
 * named two. A candidate without a number is named by its id.
 */
export const candidateReference = (candidate: TmsLoadRow, fallbackNumber: string): string => {
    if (!candidate.load_number && !fallbackNumber) return `load ${candidate.load_id}`
    const number = candidate.load_number ?? fallbackNumber
    return `#${number} (${candidate.broker ?? 'no broker'}, ${laneOf(candidate.origin, candidate.destination)})`
}

const numberOf = (row: TmsInvoiceRow): string => row.load_numbers.join(', ') || '—'
const sumOf = (rows: TmsInvoiceRow[]): number | null => {
    const amounts = rows.map((row) => row.amount_due_usd).filter((a): a is number => a !== null && a !== undefined)
    return amounts.length ? amounts.reduce((total, a) => total + a, 0) : null
}

/** Why a load is not ready, in the card's words: the one line the row's state shows. */
const blockerOf = (row: TmsInvoiceRow): string => {
    const first = byFixOrder(findingsOf(row))[0]
    return first ? first.message.trim().replace(/\.$/, '') : 'Not ready yet'
}

/** The row's fixes, each kind once: what its reasons and its documents still need. The invoice is never one of them. */
const fixesOfRow = (row: TmsInvoiceRow): RowAction[] => fixesOf(findingsOf(row), row.documents).filter((step) => step.kind !== 'invoice')

/** One load per block: the first row of each load wins, so a load drawn in two groups shows once. */
const oncePerLoad = (rows: TmsInvoiceRow[]): TmsInvoiceRow[] => {
    const seen = new Set<string>()
    return rows.filter((row) => {
        const key = loadKeyOf(row) ?? row.invoice_id ?? ''
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

interface ReadyRowProps {
    row: TmsInvoiceRow
    selected: boolean
    selectable: boolean
    onToggle: (on: boolean) => void
    onInvoice: () => void
    onSend: (message: string) => void
}

/** A Ready row: its checkbox, and in the action column its Invoice (when the user can create invoices) with any fix behind "⋯". */
const ReadyRow = ({ row, selected, selectable, onToggle, onInvoice, onSend }: ReadyRowProps) => {
    const dimmed = useUpdatedBelow(loadKeyOf(row))
    // An invoiceable load bills its $0 line haul as a warning, so its rate is not a fix offered here (as on the Ready list).
    const fixes = fixesOfRow(row).filter((step) => step.kind !== 'set_rate')
    const invoice = selectable ? (
        <Button size="sm" variant="ghost" onClick={onInvoice}>
            {invoiceLabelOf(row.tonu)}
        </Button>
    ) : undefined
    const number = row.load_numbers[0]
    return (
        <LoadRow
            number={numberOf(row)}
            title={row.broker ?? '—'}
            state={readyStateOf(row)}
            documents={documentSlotsOf(row.documents)}
            amount={row.amount_due_usd}
            dimmed={dimmed}
            redFlag={row.red_flagged}
            lead={selectable ? <Checkbox ariaLabel={`Select #${numberOf(row)}`} checked={selected} onChange={onToggle} /> : undefined}
            action={
                number && (fixes.length > 0 || invoice) ? (
                    <LoadFixActions loadNumber={number} loadId={row.load_id} actions={fixes} onSend={onSend} lead={invoice} />
                ) : (
                    invoice
                )
            }
        />
    )
}

interface ReadinessViewProps {
    readiness: TmsLoadsReadiness
    onSend: (message: string) => void
}

/**
 * `tms_invoices.worklist` (load_numbers) as one grouped answer (design §3, §6, §7): Ready, with checkboxes, "Invoice all" and the selection bar;
 * Needs documents, with the fix in the action column; Billed through Amazon; Invoiced, which also takes the Ready loads the chat has just
 * invoiced; Not delivered; and the names that matched no load or several, each sent as an unambiguous reference. Every group is drawn
 * expanded; a long Invoiced group shows its first rows and "Show N more" at the bottom of the block. "Invoice" opens the approval
 * (`InvoiceApproval`) inside this block; only its "Create invoice" sends the loads to the agent.
 */
export const ReadinessView = ({ readiness, onSend }: ReadinessViewProps) => {
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [approval, setApproval] = useState<TmsInvoiceRow[] | null>(null)
    const [showAllInvoiced, setShowAllInvoiced] = useState(false)
    const canInvoice = useCanCreateInvoices()
    const invoicedAs = useInvoicedLookup()
    const fresh = useFreshRows((state) => state.rows)
    const current = (row: TmsInvoiceRow): TmsInvoiceRow => (row.load_id && fresh[row.load_id]) || row
    const { ready: readyDrawn, needs_documents: needsDrawn, invoiced, not_delivered: away, not_found: missing, ambiguous } = readiness
    // A card stored before the Amazon group existed has none: it answers no Amazon loads, never a crash.
    const amazon = oncePerLoad(amazonRowsOf(readiness.billed_through_amazon))
    // Grouped by the rows as they are now: a Ready load the chat has invoiced (in this chat, or read again as invoiced) moves to Invoiced.
    const allRows = [...readyDrawn, ...needsDrawn].map(current)
    const isInvoiced = (row: TmsInvoiceRow) => row.status_group === 'invoiced' || !!invoicedAs(row)
    const ready = allRows.filter((row) => row.invoiceable && !isInvoiced(row))
    const needs = allRows.filter((row) => !row.invoiceable && !isInvoiced(row))
    const invoicedRows = oncePerLoad([...allRows.filter(isInvoiced), ...invoiced.rows])
    // The group's count is the server's whole count, plus the loads the chat invoiced here that the server's count does not hold.
    const invoicedHeld = new Set(invoiced.rows.map((row) => loadKeyOf(row) ?? row.invoice_id ?? ''))
    const invoicedCount = invoiced.count + invoicedRows.filter((row) => !invoicedHeld.has(loadKeyOf(row) ?? row.invoice_id ?? '')).length
    const invoicedShown = showAllInvoiced ? invoicedRows : invoicedRows.slice(0, INVOICED_SHOWN)
    const chosen = ready.filter((row) => selected.has(loadKeyOf(row) ?? ''))

    const toggle = (row: TmsInvoiceRow, on: boolean) =>
        setSelected((prev) => {
            const next = new Set(prev)
            const key = loadKeyOf(row) ?? ''
            if (on) next.add(key)
            else next.delete(key)
            return next
        })

    const approve = (rows: TmsInvoiceRow[]) => setApproval(rows)
    const send = (rows: TmsInvoiceRow[]) => {
        onSend(invoiceLoadsMessage(rows.flatMap((row) => row.load_numbers)))
        setApproval(null)
        setSelected(new Set())
    }

    return (
        <div className="flex flex-col gap-3 text-xs">
            {ready.length > 0 && (
                <section aria-label="Ready to invoice" className="flex flex-col">
                    <header className="flex items-center justify-between gap-2 pb-1">
                        <span className="font-medium">
                            Ready · {ready.length}
                            {sumOf(ready) !== null && <span className="text-foreground-muted"> · {formatUsd(sumOf(ready))}</span>}
                        </span>
                        {canInvoice && !approval && (
                            <Button size="sm" variant="outline" onClick={() => approve(ready)}>
                                Invoice all {ready.length} · {formatUsd(sumOf(ready))}
                            </Button>
                        )}
                    </header>
                    {!canInvoice && <p className="pb-1 text-foreground-muted">{CANNOT_INVOICE_NOTE}</p>}
                    {ready.map((row) => (
                        <ReadyRow
                            key={loadKeyOf(row)}
                            row={row}
                            selected={selected.has(loadKeyOf(row) ?? '')}
                            selectable={canInvoice}
                            onToggle={(on) => toggle(row, on)}
                            onInvoice={() => approve([row])}
                            onSend={onSend}
                        />
                    ))}
                    {canInvoice && chosen.length > 0 && !approval && (
                        <div role="status" className="mt-1 flex items-center gap-2 rounded-xl bg-surface-secondary px-3 py-2">
                            <span>
                                {chosen.length} selected · {formatUsd(sumOf(chosen))}
                            </span>
                            <Button size="sm" className="ml-auto" onClick={() => approve(chosen)}>
                                Invoice {chosen.length}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                                Clear
                            </Button>
                        </div>
                    )}
                    {canInvoice && approval && (
                        <InvoiceApproval
                            loadNumbers={approval.flatMap((row) => row.load_numbers)}
                            total={sumOf(approval)}
                            onCreate={() => send(approval)}
                            onCancel={() => setApproval(null)}
                        />
                    )}
                </section>
            )}

            {needs.length > 0 && (
                <section aria-label="Needs documents" className="flex flex-col">
                    <header className="pb-1 font-medium">Needs documents · {needs.length}</header>
                    {needs.map((row) => {
                        const fixes = fixesOfRow(row)
                        const number = row.load_numbers[0]
                        return (
                            <LoadRow
                                key={loadKeyOf(row)}
                                number={numberOf(row)}
                                title={row.broker ?? '—'}
                                state={`Can't invoice yet: ${blockerOf(row)}`}
                                documents={documentSlotsOf(row.documents)}
                                amount={row.amount_due_usd}
                                redFlag={row.red_flagged}
                                lead={<Checkbox ariaLabel={`#${numberOf(row)} cannot be invoiced yet`} disabled checked={false} />}
                                action={
                                    number && fixes.length > 0 ? (
                                        <LoadFixActions loadNumber={number} loadId={row.load_id} actions={fixes} onSend={onSend} />
                                    ) : undefined
                                }
                            />
                        )
                    })}
                </section>
            )}

            {amazon.length > 0 && (
                <section aria-label="Billed through Amazon" className="flex flex-col">
                    <header className="pb-1 font-medium text-foreground-muted">Billed through Amazon · {amazon.length}</header>
                    {amazon.map((row) => (
                        <LoadRow key={loadKeyOf(row)} number={numberOf(row)} title={row.broker ?? '—'} state={AMAZON_STATE} amount={row.amount_due_usd} />
                    ))}
                </section>
            )}

            {invoicedCount > 0 && (
                <section aria-label="Already invoiced" className="flex flex-col">
                    <header className="pb-1 font-medium">
                        Invoiced · {invoicedCount}
                        {invoiced.amount_usd !== null && invoicedRows.length === invoiced.rows.length && (
                            <span className="text-foreground-muted"> · {formatUsd(invoiced.amount_usd)}</span>
                        )}
                    </header>
                    {invoicedShown.map((row) => {
                        const number = row.load_numbers[0]
                        // An invoiced load still takes its POD: a factor can ask for it after invoicing.
                        const podFixes = documentActions(row.documents).filter((step) => step.kind === 'upload_pod' || step.kind === 'review_pod')
                        return (
                            <LoadRow
                                key={`${loadKeyOf(row)}-${row.invoice_id ?? ''}`}
                                number={numberOf(row)}
                                title={row.broker ?? '—'}
                                state={<InvoicedLabel number={invoicedAs(row) ?? row.invoice_number} />}
                                documents={documentSlotsOf(row.documents)}
                                amount={row.amount_due_usd}
                                action={
                                    number && podFixes.length > 0 ? (
                                        <LoadFixActions loadNumber={number} loadId={row.load_id} actions={podFixes} onSend={onSend} />
                                    ) : undefined
                                }
                            />
                        )
                    })}
                </section>
            )}

            {away.length > 0 && (
                <section aria-label="Not delivered" className="flex flex-col">
                    <header className="pb-1 font-medium">Not delivered · {away.length}</header>
                    {away.map((row: TmsLoadRow) => (
                        <LoadRow
                            key={row.load_id}
                            number={row.load_number ?? '—'}
                            title={row.broker ?? '—'}
                            state={`${LOAD_STAGE_LABEL[row.status] ?? row.status} · ${laneOf(row.origin, row.destination)}`}
                            documents={documentSlotsOf(undefined, true)}
                            amount={row.broker_rate_usd}
                        />
                    ))}
                </section>
            )}

            {invoicedRows.length > INVOICED_SHOWN && (
                <Button size="sm" variant="ghost" className="self-start" onClick={() => setShowAllInvoiced((on) => !on)}>
                    {showAllInvoiced ? 'Show fewer' : `Show ${invoicedRows.length - INVOICED_SHOWN} more`}
                </Button>
            )}

            {(missing.length > 0 || ambiguous.length > 0) && (
                <section aria-label="Not found or ambiguous" className="flex flex-col gap-2">
                    {missing.map((number) => (
                        <p key={number} className="text-foreground-muted">
                            No load #{number} matches.
                        </p>
                    ))}
                    {ambiguous.map((match) => (
                        <div key={match.load_number} className="flex flex-col gap-1">
                            <p className="text-foreground-muted">#{match.load_number} matches more than one load. Which one?</p>
                            <div className="flex flex-wrap gap-1.5">
                                {match.candidates.map((candidate) => (
                                    <Button
                                        key={candidate.load_id}
                                        size="sm"
                                        variant="outline"
                                        onClick={() => onSend(candidateReference(candidate, match.load_number))}
                                    >
                                        #{candidate.load_number ?? '—'} · {candidate.broker ?? '—'}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    ))}
                </section>
            )}
        </div>
    )
}
